import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  IoHomeOutline,
  IoChatbubblesOutline,
  IoMegaphoneOutline,
  IoStorefrontOutline,
  IoTicketOutline,
  IoLocationSharp,
  IoWarningOutline,
  IoVideocamOutline,
  IoWalletOutline,
  IoDocumentTextOutline,
  IoListCircleOutline,
  IoCarSportOutline,
  IoCalendarOutline,
  IoCashOutline,
  IoPeopleOutline,
  IoBriefcaseOutline,
  IoSettingsOutline,
  IoLogOutOutline,
  IoConstructOutline,
  IoReceiptOutline,
  IoShieldCheckmarkOutline,
  IoCubeOutline,
  IoArchiveOutline,
  IoBusinessOutline,
  IoKeypadOutline,
  IoAnalyticsOutline,
  IoStatsChartOutline,
  IoCalculatorOutline,
  IoAppsOutline,
  IoFolderOpenOutline,
  IoCardOutline,
  IoFileTrayFullOutline,
  IoLockOpenOutline
} from 'react-icons/io5';
import kondoLogoFull from '../../assets/Kondify letra negra sin fondo.png';

import { db } from '../../firebaseConfig';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { isModuleEnabled } from '../../utils/saasConfig';

const KondoSidebar = ({ activeRoute }) => {
  const navigate = useNavigate();
  const { currentUser, userData, logout, isMoroso } = useAuth();

  // Live badge states
  const [unreadSanciones, setUnreadSanciones] = React.useState(0);
  const [unreadConvenios, setUnreadConvenios] = React.useState(0);
  const [pendingReports, setPendingReports] = React.useState(0);

  React.useEffect(() => {
    if (!currentUser || !userData) return;

    let unsubSanciones = () => {};
    let unsubConvenios = () => {};
    let unsubReports = () => {};

    if (userData.rol === 'residente') {
      // Sanciones unread count
      unsubSanciones = onSnapshot(
        query(collection(db, "apercibimientos_multas"), where("residenteId", "==", currentUser.uid), where("leido", "==", false)),
        (snap) => setUnreadSanciones(snap.size)
      );

      // Convenios unread count
      unsubConvenios = onSnapshot(
        query(collection(db, "convenios"), where("condominioId", "==", userData.condominioId), where("residenteId", "==", currentUser.uid), where("estatus", "==", "pendiente_firma")),
        (snap) => setUnreadConvenios(snap.size)
      );
    }

    if (['admin', 'comite'].includes(userData.rol)) {
      // Admin pending reports count
      unsubReports = onSnapshot(
        query(collection(db, "reportes"), where("condominioId", "==", userData.condominioId), where("estatus", "==", "pendiente")),
        (snap) => setPendingReports(snap.size)
      );
    }

    return () => {
      unsubSanciones();
      unsubConvenios();
      unsubReports();
    };
  }, [currentUser, userData]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (e) {
      console.error(e);
    }
  };

  const hasPermission = (key) => {
    if (userData?.tipoResidente === 'Propietario' || userData?.rol === 'admin' || userData?.rol === 'comite') return true;
    if (!userData?.permisosInquilino) return false;
    return userData.permisosInquilino[key] === true;
  };

  const handleRestrictedAccess = (e, path) => {
    if (isMoroso) {
      e.preventDefault();
      alert('Acceso denegado. Tienes adeudos pendientes, por favor ve a la sección "Pagar Cuota"');
      navigate('/pagar-cuota');
    } else {
      navigate(path);
    }
  };

  const isActive = (route) => activeRoute === route ? 'ks-sidebar-item active' : 'ks-sidebar-item';

  const renderModule = (path, icon, label, onClick = null, badgeCount = 0) => (
    <button
      key={`${path}-${label}`}
      data-path={path}
      className={isActive(path)}
      onClick={onClick || (() => navigate(path))}
      style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%' }}
    >
      {icon}
      <span>{label}</span>
      {badgeCount > 0 && (
        <span className="ks-sidebar-badge" style={{
          marginLeft: 'auto',
          marginRight: '8px',
          background: '#ef4444',
          color: 'white',
          borderRadius: '10px',
          padding: '2px 8px',
          fontSize: '0.7rem',
          fontWeight: 'bold',
          lineHeight: '1.2'
        }}>{badgeCount}</span>
      )}
    </button>
  );

  const getModules = () => {
    const modules = [];

    // 1. Dashboard (Always first)
    modules.push(renderModule('/app', <IoHomeOutline size={20} />, 'Dashboard'));

    if (!userData) return modules;

    // 2. Role-based modules (Mirroring HomeScreen.js logic and order)

    // --- RESIDENTE --- (Inclusive of Comite basics)
    if (['residente', 'comite'].includes(userData.rol)) {
      modules.push(renderModule('/muro', <IoChatbubblesOutline size={20} />, 'Muro Social'));
      modules.push(renderModule('/marketplace', <IoStorefrontOutline size={20} />, 'Marketplace'));
      if (hasPermission('accesos')) modules.push(renderModule('/accesos', <IoTicketOutline size={20} />, 'Accesos'));
      if (hasPermission('estacionamientos')) modules.push(renderModule('/estacionamientos', <IoLocationSharp size={20} />, 'Estacionamientos'));
      modules.push(renderModule('/mis-reportes', <IoWarningOutline size={20} />, 'Reportar'));
      modules.push(renderModule('/seguridad', <IoVideocamOutline size={20} />, 'Seguridad'));
      if (hasPermission('cuotas')) modules.push(renderModule('/pagar-cuota', <IoWalletOutline size={20} />, 'Pagar Cuota'));
      if (hasPermission('finanzas')) modules.push(renderModule('/finanzas', <IoDocumentTextOutline size={20} />, 'Finanzas Condominio'));
      modules.push(renderModule('/votaciones', <IoStatsChartOutline size={20} />, 'Votaciones'));
      if (hasPermission('vehiculos')) modules.push(renderModule('/vehiculos', <IoCarSportOutline size={20} />, 'Mis Vehículos'));
      modules.push(renderModule('/apercibimientos', <IoWarningOutline size={20} />, 'Mis Sanciones', null, unreadSanciones));
      modules.push(renderModule('/convenios', <IoDocumentTextOutline size={20} />, 'Mis Convenios', null, unreadConvenios));
      modules.push(renderModule('/documentos', <IoFolderOpenOutline size={20} />, 'Documentación'));

      // If provider
      if (userData.proveedorId) {
        modules.push(renderModule('/proveedor/cotizaciones', <IoBriefcaseOutline size={20} />, 'Solicitudes'));
        modules.push(renderModule('/proveedor/servicios', <IoListCircleOutline size={20} />, 'Mis Servicios'));
        modules.push(renderModule('/proveedor/calendario', <IoCalendarOutline size={20} />, 'Mi Calendario'));
        modules.push(renderModule('/proveedor/suscripcion', <IoCardOutline size={20} />, 'Gestionar Plan'));
      }
    }

    // --- COMITE ADDONS ---
    if (userData.rol === 'comite') {
      modules.push(renderModule('/gestion-finanzas', <IoDocumentTextOutline size={20} />, 'Transparencia'));
      modules.push(renderModule('/licitaciones', <IoDocumentTextOutline size={20} />, 'Licitaciones de Compra'));
      modules.push(renderModule('/admin/votaciones', <IoStatsChartOutline size={20} />, 'Votaciones (Admin)'));
      modules.push(renderModule('/admin/muro', <IoChatbubblesOutline size={20} />, 'Moderación Muro'));
      modules.push(renderModule('/gestion-facturas', <IoReceiptOutline size={20} />, 'Aprobaciones', () => navigate('/gestion-facturas', { state: { viewContext: 'aprobaciones' } })));
      modules.push(renderModule('/gestion-reportes', <IoWarningOutline size={20} />, 'Reportes', null, pendingReports));
      modules.push(renderModule('/apercibimientos', <IoWarningOutline size={20} />, 'Apercibimientos y Multas'));
      modules.push(renderModule('/configuracion-condominio', <IoSettingsOutline size={20} />, 'Configuración'));
      modules.push(renderModule('/gestion-usuarios', <IoPeopleOutline size={20} />, 'Directorio'));
      modules.push(renderModule('/paqueteria', <IoCubeOutline size={20} />, 'Paquetería', () => navigate('/paqueteria?mode=admin')));
      modules.push(renderModule('/proveedores-generales', <IoBusinessOutline size={20} />, 'Prov. Generales'));
      modules.push(renderModule('/bitacora', <IoListCircleOutline size={20} />, 'Bitácora Accesos'));
    }

    // --- ADMIN & CONDOMINIUM MASTERUSER ---
    if (userData.rol === 'admin' || (userData.rol === 'masteruser' && userData.condominioId && userData.condominioId !== 'GLOBAL')) {
      modules.push(renderModule('/gestion-avisos', <IoMegaphoneOutline size={20} />, 'Publicar Avisos'));
      modules.push(renderModule('/licitaciones', <IoDocumentTextOutline size={20} />, 'Licitaciones de Compra'));
      modules.push(renderModule('/admin/votaciones', <IoStatsChartOutline size={20} />, 'Votaciones'));
      modules.push(renderModule('/admin/muro', <IoChatbubblesOutline size={20} />, 'Moderación Muro'));
      modules.push(renderModule('/gestion-reportes', <IoWarningOutline size={20} />, 'Reportes', null, pendingReports));
      modules.push(renderModule('/apercibimientos', <IoWarningOutline size={20} />, 'Apercibimientos y Multas'));
      modules.push(renderModule('/gestion-vehiculos', <IoCarSportOutline size={20} />, 'Control Vehicular'));
      modules.push(renderModule('/admin/validacion-proveedores', <IoBriefcaseOutline size={20} />, 'Solicitudes Mkt.'));
      modules.push(renderModule('/estacionamientos', <IoLocationSharp size={20} />, 'Estacionamientos'));
      modules.push(renderModule('/paqueteria', <IoCubeOutline size={20} />, 'Paquetería'));
      modules.push(renderModule('/admin/accesos', <IoLockOpenOutline size={20} />, 'Accesos'));
      modules.push(renderModule('/seguridad', <IoShieldCheckmarkOutline size={20} />, 'Seguridad'));
      modules.push(renderModule('/proveedores-generales', <IoBusinessOutline size={20} />, 'Prov. Generales'));
      modules.push(renderModule('/nominas-dashboard', <IoPeopleOutline size={20} />, 'Nóminas'));
      modules.push(renderModule('/gestion-cuotas', <IoCashOutline size={20} />, 'Gestión de Cuotas'));
      modules.push(renderModule('/gestion-finanzas', <IoDocumentTextOutline size={20} />, 'Reportes Financieros'));
      modules.push(renderModule('/finanzas/banco', <IoWalletOutline size={20} />, 'Bancos'));
      modules.push(renderModule('/obligaciones-fiscales', <IoFileTrayFullOutline size={20} />, 'Declaraciones y SAT'));
      modules.push(renderModule('/gestion-facturas', <IoReceiptOutline size={20} />, 'Facturas y Gastos', () => navigate('/gestion-facturas', { state: { viewContext: 'gastos' } })));
      modules.push(renderModule('/bitacora', <IoListCircleOutline size={20} />, 'Bitácora Accesos'));
      modules.push(renderModule('/gestion-usuarios', <IoPeopleOutline size={20} />, 'Usuarios'));
      modules.push(renderModule('/documentos', <IoFolderOpenOutline size={20} />, 'Documentación'));
      modules.push(renderModule('/gestion-amenidades', <IoConstructOutline size={20} />, 'Amenidades'));
      modules.push(renderModule('/admin/configuracion-fiscal', <IoSettingsOutline size={20} />, 'Configuración'));

      if (userData.rol === 'masteruser') {
        modules.push(renderModule('/gestion-facturas-aprobaciones', <IoReceiptOutline size={20} />, 'Aprobaciones (Comité)', () => navigate('/gestion-facturas', { state: { viewContext: 'aprobaciones' } })));
        modules.push(renderModule('/configuracion-condominio', <IoSettingsOutline size={20} />, 'Reglas y Límites'));
      }
    }

    // --- VIGILANTE ---
    if (userData.rol === 'vigilante') {
      modules.push(renderModule('/seguridad', <IoShieldCheckmarkOutline size={20} />, 'Seguridad'));
      modules.push(renderModule('/paqueteria', <IoArchiveOutline size={20} />, 'Lockers'));
      modules.push(renderModule('/estacionamientos', <IoLocationSharp size={20} />, 'Estacionamientos'));
      modules.push(renderModule('/proveedores-generales', <IoBusinessOutline size={20} />, 'Prov. Generales'));
    }

    // --- TRABAJADOR ---
    if (userData.rol === 'trabajador') {
      modules.push(renderModule('/trabajador/dashboard', <IoConstructOutline size={20} />, 'Panel Operativo'));
    }

    // --- SUPERUSER & GLOBAL MASTERUSER ---
    if (userData.rol === 'superuser' || (userData.rol === 'masteruser' && (!userData.condominioId || userData.condominioId === 'GLOBAL'))) {
      modules.push(renderModule('/superuser/empresa', <IoBusinessOutline size={20} />, 'Perfil Corporativo'));
      modules.push(renderModule('/superuser/validacion-proveedores', <IoBriefcaseOutline size={20} />, 'Validación Global'));
      modules.push(renderModule('/superuser/dashboard', <IoReceiptOutline size={20} />, 'Facturación SaaS'));
      modules.push(renderModule('/superuser/accounting', <IoBusinessOutline size={20} />, 'Accounting SaaS'));
      modules.push(renderModule('/superuser/crm', <IoPeopleOutline size={20} />, 'SaaS CRM Central'));
      modules.push(renderModule('/superuser/marketplace', <IoStorefrontOutline size={20} />, 'Marketplace B2B'));
      modules.push(renderModule('/superuser/marketplace/categorias', <IoListCircleOutline size={20} />, 'Rubros Marketplace'));
      modules.push(renderModule('/superuser/analytics', <IoAnalyticsOutline size={20} />, 'Inteligencia de Negocio'));
      modules.push(renderModule('/superuser/projections', <IoCalculatorOutline size={20} />, 'Proyecciones P&L'));
      modules.push(renderModule('/superuser/market-planes', <IoCalendarOutline size={20} />, 'Tiers Marketplace'));
      modules.push(renderModule('/superuser/hr/dashboard', <IoPeopleOutline size={20} />, 'RH y nóminas'));
    }

    return modules.filter(mod => {
      const path = mod.props['data-path'];
      if (!path) return true;
      if (path.startsWith('/superuser')) return true;
      if (path === '/' || path === '/app' || path === '/perfil' || path === '/reportar' || path === '/mis-reportes' || path === '/pagar-cuota' || path === '/finanzas' || path === '/metodos-pago') return true;
      return isModuleEnabled(userData?.condoData, path);
    });
  };

  return (
    <aside className="ks-sidebar">
      <div className="ks-sidebar-logo">
        <img src={kondoLogoFull} alt="Kondify" className="ks-sidebar-full-logo" />
      </div>
      <div className="ks-sidebar-scroll-area">
        <nav className="ks-sidebar-nav">
          {getModules()}
        </nav>
      </div>
      <div className="ks-sidebar-footer">
        {(userData?.rol === 'masteruser' || (userData?.rol === 'superuser' && userData?.condominioId !== 'GLOBAL')) && (
          <button className={isActive('/superuser/master-portal')} onClick={() => navigate('/superuser/master-portal')}>
            <IoAppsOutline size={20} /><span>Portales</span>
          </button>
        )}
        <button className={isActive('/perfil')} onClick={() => navigate('/perfil')}>
          <IoSettingsOutline size={20} /><span>Perfil</span>
        </button>
        <button className="ks-sidebar-item danger" onClick={handleLogout}>
          <IoLogOutOutline size={20} /><span>Salir</span>
        </button>
      </div>
    </aside>
  );
};

export default KondoSidebar;
